import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormLabel, FormGroup, FormControl, FilledInput, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import dayjs from 'dayjs';

import Loader from '../../common/loader';
import DateRange from '../../common/date-range';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { get } from '../../utils/api-services';
import DownloadExcel from '../../common/download-excel';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: 20
    },
    tableContainer: {
        width: '100%'
    }
}));

const defSupplier = { id: 0, name: 'All suppliers' };
const defLocation = { id: 0, name: 'All locations' };

const TicketSaleReport = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const [state, setState] = useState({
        loading: true,
        suppliers: [],
        locations: [],
        data: [],
        filter: {
            startDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
            endDate: dayjs().format('YYYY-MM-DD'),
            dateRange: `${dayjs().subtract(1, 'day').format('MM/DD/YYYY')} - ${dayjs().format('MM/DD/YYYY')}`,
            supplier: defSupplier,
            location: defLocation
        },
        rangeModal: {
            open: false,
            range: [
                {
                    startDate: new Date(dayjs().subtract(1, 'day').toDate()),
                    endDate: new Date(),
                    key: 'main'
                }
            ]
        },
        permissions: {}
    });

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    useEffect(() => {
        window.document.title = 'Ticket sale report';

        makeRequest();
    }, []);

    useEffect(() => {
        setState(prev => ({ ...prev, data: [] }));
    }, [state.filter]);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/reports/indexTicketSale');
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                suppliers: response?.suppliers ?? [],
                locations: response?.locations ?? []
            }));
        } else {
            console.log('[TicketSaleReport] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleDateChange = item => {
        const { startDate, endDate } = item.main;
        const dateRange = `${dayjs(startDate.toISOString()).format('MM/DD/YYYY')} - ${dayjs(endDate.toISOString()).format('MM/DD/YYYY')}`;
        setState(prev => ({
            ...prev,
            filter: {
                ...prev.filter,
                dateRange,
                startDate: dayjs(startDate.toISOString()).format('YYYY-MM-DD'),
                endDate: dayjs(endDate.toISOString()).format('YYYY-MM-DD')
            },
            rangeModal: { ...prev.rangeModal, range: [item.main] }
        }));
    }

    const handleModal = () => setState(prev => ({ ...prev, rangeModal: { ...prev.rangeModal, open: !prev.rangeModal.open } }));

    const handleChangeAutocomplete = (key, value) => setState(prev => ({ ...prev, filter: { ...prev.filter, [key]: value } }));

    const search = async () => {
        showLoader(true);
        const response = await get('/reports/searchTicketSale', state.filter);
        if (response.status === 200) {
            setState(prev => ({ ...prev, data: response?.data ?? [] }));

            if (!response?.data[0]) showToast('There are no tickets with these filters', 'warning');
        } else {
            console.log('[TicketSaleReport] search error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const onReset = () => setState(prev => ({ ...prev, data: [] }));

    const excelColumns = [
        { label: 'Code', value: 'code' },
        { label: 'Date', value: 'date' },
        { label: 'Sede code', value: 'sede.code' },
        { label: 'Sede name', value: 'sede.name' },
        { label: 'Supplier code', value: 'supplier.code' },
        { label: 'Supplier name', value: 'supplier.name' },
        { label: 'Location code', value: 'location.code' },
        { label: 'Location name', value: 'location.name' },
        { label: 'Product code', value: 'product_type_ref.product.code' },
        { label: 'Product name', value: 'product_type_ref.product.name' },
        { label: 'Type', value: 'product_type_ref.type.name' },
        { label: 'Qty', value: 'qty' },
        { label: 'Product qty', value: 'product_qty' },
        { label: 'Total qty', value: 'total_qty' },
        { label: 'Price', value: 'price' },
        { label: 'Total', value: 'total' },
        { label: 'Check', value: 'check.code' },
        { label: 'Notes', value: 'notes' }
    ];

    if (state.loading) return <Loader />;

    return (
        <div className={classes.root}>
            <Grid container>
                <Grid item xs={6} lg={3}>
                    <FormGroup onClick={handleModal}>
                        <FormLabel>{'Date range'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                autoComplete='off'
                                type='text'
                                placeholder='Date range'
                                name='range'
                                value={state.filter.dateRange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Supplier'}</FormLabel>
                        <Autocomplete
                            options={state.suppliers}
                            value={state.filter.supplier}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleChangeAutocomplete('supplier', value)}
                            renderInput={params => <TextField {...params} placeholder='Supplier' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Location'}</FormLabel>
                        <Autocomplete
                            options={state.locations}
                            value={state.filter.location}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleChangeAutocomplete('location', value)}
                            renderInput={params => <TextField {...params} placeholder='Location' />}
                        />
                    </FormGroup>
                </Grid>
            </Grid>

            <div className={classes.buttonContainer}>
                <DownloadExcel fileName='ticket-sale-report' sheetName='Ticket sales' data={state.data} columns={excelColumns} onClick={search} onReset={onReset} />
            </div>

            <DateRange
                open={state.rangeModal.open}
                range={state.rangeModal.range}
                onClose={handleModal}
                onChange={handleDateChange}
            />
        </div>
    );
}

export default TicketSaleReport;